import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getUsersAsync } from "../../../slices/user";
import {
  createHseSafetyInspection,
  updateHseSafetyInspection,
} from "../../../api/hse/safetyInspections";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { PMField } from "../../utils/PMField";
import PMCombobox from "../../utils/PMCombobox";
import { CustomerTag } from "../../utils/moduleTags/customerTag";
import { EmployeeTag } from "../../utils/moduleTags/employeeTag";
import { ProjectTag } from "../../utils/moduleTags/projectTag";
import { DocumentUploadSectionV2 } from "../Media/DocumentUploadSectionV2";
import { TinyDocumentSection } from "../documents/tinyDocumentSection";
import { DepartmentSelector } from "../../utils/moduleSelectors/departmentSelector";
import { SafetyAreaSelector } from "../../utils/moduleSelectors/safetyAreaSelector";
import { SafetyCheckpointSelector } from "../../utils/moduleSelectors/safetyCheckpointSelector";
import { MultiEmployeeSelector } from "../../utils/moduleSelectors/multiEmployeeSelector";
import { MultiContactSelector } from "../../utils/moduleSelectors/multiContactSelector";

export const HseSafetyInspectionForm = ({ inspection, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const itemState = useSelector((store) => store.users);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showMultiEmployeeSelector, setShowMultiEmployeeSelector] =
    useState(false);
  const [showMultiContactSelector, setShowMultiContactSelector] =
    useState(false);
  const [showSafetyAreaSelector, setShowSafetyAreaSelector] = useState(false);
  const [showSafetyCheckpointSelector, setShowSafetyCheckpointSelector] =
    useState(false);
  const [currentAreaId, setCurrentAreaId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedCheckpoints, setSelectedCheckpoints] = useState([]);

  // Status options
  const statusOptions = [
    { value: "draft", title: t("Kladd") },
    { value: "in_progress", title: t("Under gjennomføring") },
    { value: "completed", title: t("Gjennomført") },
    { value: "closed", title: t("Lukket") },
  ];

  // Role options for participants
  const participantRoleOptions = [
    { value: "participant", title: t("Deltaker") },
    { value: "leader", title: t("Leder") },
    { value: "observer", title: t("Observatør") },
  ];

  // Role options for contacts
  const contactRoleOptions = [
    { value: "participant", title: t("Deltaker") },
    { value: "observer", title: t("Observatør") },
    { value: "responsible", title: t("Ansvarlig") },
  ];

  // Validation schema
  const validationSchema = Yup.object().shape({
    title: Yup.string().required(t("Tittel er påkrevd")),
    inspection_date: Yup.date().required(
      t("Dato for gjennomføring er påkrevd")
    ),
    location: Yup.string().required(t("Lokasjon er påkrevd")),
    description: Yup.string(),
    status: Yup.string().required(t("Status er påkrevd")),
  });

  // Initialize form data
  const initialValues = {
    title: inspection?.title || "",
    inspection_date:
      inspection?.inspection_date || new Date().toISOString().split("T")[0],
    department_id: inspection?.department?.id || "",
    location: inspection?.location || "",
    description: inspection?.description || "",
    status: inspection?.status || "draft",
    project_id: inspection?.project?.id || "",
    customer_id: inspection?.customer?.id || "",
  };

  // Fetch users when component mounts
  useEffect(() => {
    if (!itemState.data || itemState.data.length === 0) {
      dispatch(
        getUsersAsync({
          filters: [{ name: "EMPLOYEE", value: true }],
        })
      );
    }
  }, []);

  // Initialize participants and contacts from inspection
  useEffect(() => {
    if (inspection?.participants) {
      setParticipants(inspection.participants);
    }
    if (inspection?.contacts) {
      setContacts(inspection.contacts);
    }
  }, [inspection]);

  const UploaderCallback = (newDocuments, formik) => {
    setDocuments(newDocuments);
  };

  const handleParticipantsSelected = (selectedUsers) => {
    setParticipants(selectedUsers);
    setShowMultiEmployeeSelector(false);
  };

  const handleContactsSelected = (selectedContacts) => {
    setContacts(selectedContacts);
    setShowMultiContactSelector(false);
  };

  const handleSafetyAreasSelected = (selectedAreas) => {
    setSelectedAreas(selectedAreas);
    setShowSafetyAreaSelector(false);
  };

  const handleSafetyCheckpointsSelected = (selectedCheckpoints) => {
    // Merge with existing checkpoints, avoiding duplicates
    const existingCheckpoints = selectedCheckpoints.filter(
      (c) => c.hse_safety_area?.id !== currentAreaId
    );
    setSelectedCheckpoints([...existingCheckpoints, ...selectedCheckpoints]);
    setShowSafetyCheckpointSelector(false);
  };

  const handleSubmit = async (values, helpers) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        participants: participants.map((p) => ({
          user_id: p.id,
          role: p.role || "participant",
          notes: p.notes || "",
        })),
        contacts: contacts.map((c) => ({
          contact_id: c.id,
          role: c.role || "participant",
          notes: c.notes || "",
        })),
        checkpoints: selectedCheckpoints.map((c) => ({
          checkpoint_id: c.id,
        })),
      };

      let response;
      if (inspection?.id) {
        response = await updateHseSafetyInspection(inspection.id, payload);
        toast.success(t("Vernerunde oppdatert"));
      } else {
        response = await createHseSafetyInspection(payload);
        toast.success(t("Vernerunde opprettet"));
      }

      if (onSubmit) {
        onSubmit(response.data.data);
      }
    } catch (error) {
      console.error("Error saving safety inspection:", error);
      toast.error(
        error.response?.data?.errors?.[0] || t("Feil ved lagring av vernerunde")
      );
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <div className="space-y-6">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Om vernerunden")}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PMField
                  label={t("Tittel")}
                  name="title"
                  type="text"
                  required
                />

                <PMField
                  label={t("Dato for gjennomføring")}
                  name="inspection_date"
                  type="date"
                  required
                />

                <DepartmentSelector
                  label={t("Avdeling")}
                  name="department_id"
                  value={values.department_id}
                  onChange={(value) => setFieldValue("department_id", value)}
                />

                <PMField
                  label={t("Lokasjon")}
                  name="location"
                  type="text"
                  required
                />

                <div className="md:col-span-2">
                  <PMField
                    label={t("Beskrivelse")}
                    name="description"
                    type="textarea"
                    rows={3}
                  />
                </div>

                <ProjectTag
                  label={t("Prosjekt")}
                  name="project_id"
                  value={values.project_id}
                  onChange={(value) => setFieldValue("project_id", value)}
                />

                <CustomerTag
                  label={t("Kunde")}
                  name="customer_id"
                  value={values.customer_id}
                  onChange={(value) => setFieldValue("customer_id", value)}
                />

                <div className="md:col-span-2">
                  <PMCombobox
                    label={t("Status")}
                    name="status"
                    options={statusOptions}
                    value={values.status}
                    onChange={(value) => setFieldValue("status", value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Participants */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("Deltakere")}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowMultiEmployeeSelector(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t("Legg til deltakere")}
                </button>
              </div>

              {participants.length > 0 && (
                <div className="space-y-2">
                  {participants.map((participant, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <EmployeeTag user={participant} />
                        <span className="text-sm text-gray-500">
                          {participantRoleOptions.find(
                            (r) => r.value === participant.role
                          )?.title || t("Deltaker")}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setParticipants(
                            participants.filter((_, i) => i !== index)
                          )
                        }
                        className="text-red-600 hover:text-red-800"
                      >
                        {t("Fjern")}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {participants.length === 0 && (
                <p className="text-gray-500 italic">
                  {t("Ingen deltakere lagt til ennå")}
                </p>
              )}
            </div>

            {/* Contacts */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("Kontakter")}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowMultiContactSelector(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t("Legg til kontakter")}
                </button>
              </div>

              {contacts.length > 0 && (
                <div className="space-y-2">
                  {contacts.map((contact, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="font-medium">{contact.name}</span>
                        <span className="text-sm text-gray-500">
                          {contactRoleOptions.find(
                            (r) => r.value === contact.role
                          )?.title || t("Deltaker")}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setContacts(contacts.filter((_, i) => i !== index))
                        }
                        className="text-red-600 hover:text-red-800"
                      >
                        {t("Fjern")}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {contacts.length === 0 && (
                <p className="text-gray-500 italic">
                  {t("Ingen kontakter lagt til ennå")}
                </p>
              )}
            </div>

            {/* Safety Areas and Checkpoints */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("Planlegg sjekkpunkter")}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowSafetyAreaSelector(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t("Legg til verneområder")}
                </button>
              </div>

              {selectedAreas.length > 0 && (
                <div className="space-y-4">
                  {selectedAreas.map((area, areaIndex) => (
                    <div key={areaIndex} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium text-gray-900">
                          {area.title}
                        </h4>
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentAreaId(area.id);
                            setShowSafetyCheckpointSelector(true);
                          }}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {t("Velg sjekkpunkter")}
                        </button>
                      </div>

                      {/* Display selected checkpoints for this area */}
                      {selectedCheckpoints.filter(
                        (c) => c.hse_safety_area?.id === area.id
                      ).length > 0 && (
                        <div className="space-y-2">
                          <h5 className="text-sm font-medium text-gray-700">
                            {t("Valgte sjekkpunkter")}:
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            {selectedCheckpoints
                              .filter((c) => c.hse_safety_area?.id === area.id)
                              .map((checkpoint, index) => (
                                <span
                                  key={checkpoint.id}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                >
                                  {checkpoint.title}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newCheckpoints =
                                        selectedCheckpoints.filter(
                                          (c) => c.id !== checkpoint.id
                                        );
                                      setSelectedCheckpoints(newCheckpoints);
                                    }}
                                    className="ml-1 text-green-600 hover:text-green-800"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {selectedAreas.length === 0 && (
                <p className="text-gray-500 italic">
                  {t("Ingen verneområder valgt ennå")}
                </p>
              )}
            </div>

            {/* Documents */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Dokumenter")}
              </h3>

              {inspection?.id && (
                <TinyDocumentSection
                  module="hse_safety_inspection"
                  moduleParam={inspection.id}
                  title={t("Dokumenter")}
                />
              )}

              <DocumentUploadSectionV2
                module="hse_safety_inspection"
                moduleParam={inspection?.id || "new"}
                onDocumentsChange={UploaderCallback}
                documents={documents}
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t("Avbryt")}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading
                  ? t("Lagrer...")
                  : inspection?.id
                  ? t("Oppdater")
                  : t("Opprett")}
              </button>
            </div>
          </Form>
        )}
      </Formik>

      {/* Modals */}
      {showMultiEmployeeSelector && (
        <MultiEmployeeSelector
          open={showMultiEmployeeSelector}
          onClose={() => setShowMultiEmployeeSelector(false)}
          onSelected={handleParticipantsSelected}
          selectedItems={participants}
          roleOptions={participantRoleOptions}
        />
      )}

      {showMultiContactSelector && (
        <MultiContactSelector
          open={showMultiContactSelector}
          onClose={() => setShowMultiContactSelector(false)}
          onSelected={handleContactsSelected}
          selectedItems={contacts}
          roleOptions={contactRoleOptions}
        />
      )}

      {showSafetyAreaSelector && (
        <SafetyAreaSelector
          open={showSafetyAreaSelector}
          onClose={() => setShowSafetyAreaSelector(false)}
          onSelected={handleSafetyAreasSelected}
          selectedItems={selectedAreas}
        />
      )}

      {showSafetyCheckpointSelector && (
        <SafetyCheckpointSelector
          open={showSafetyCheckpointSelector}
          onClose={() => setShowSafetyCheckpointSelector(false)}
          selectedCheckpoints={selectedCheckpoints.filter(
            (c) => c.hse_safety_area?.id === currentAreaId
          )}
          onCheckpointsSelected={handleSafetyCheckpointsSelected}
          areaId={currentAreaId}
        />
      )}
    </div>
  );
};
